import { useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { evSearchCrossSiteRecoClick, evSearchCrossSiteRecoImpression } from 'events/recommendations';
import { track } from 'apis/amethyst';
import useMartyContext from 'hooks/useMartyContext';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';
import MelodyModal from 'components/common/MelodyModal';
import ImageCarousel from 'components/common/ImageCarousel';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import { constructMSAImageUrl } from 'helpers';

import { header } from 'styles/components/common/modal.scss';
import css from 'styles/components/search/crossSiteProducts.scss';

const PRODUCT_IMAGE_SIZE = 450;
const MSA_IMAGE_DIMENSION = { width: PRODUCT_IMAGE_SIZE, height: PRODUCT_IMAGE_SIZE };

export const CrossSiteProducts = ({ trustedRetailers = [], ProductComponent, getProductInfo, additionalClasses, msaImageParams, hydraSearchColorSwatches, hydraHdSearchImages }) => {
  const { testId, marketplace: { shortName } } = useMartyContext();
  const [modal, setModal] = useState(null);
  const [details, setDetails] = useState(false);

  const handleDetailsClick = useCallback(details => {
    setDetails(!details);
  }, []);

  const handleClickCard = useCallback((productObj, index, e) => {
    e.preventDefault();
    const product = { ...productObj, index };
    track(() => ([
      evSearchCrossSiteRecoClick, { product }
    ]));
    getProductInfo(productObj, { includeOos: true, errorOnOos: false }).then(response => {
      const data = { ...product, ...response };
      setModal(data);
    });
  }, [getProductInfo]);

  const handleClickStay = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: false }
    ]));
    setModal(null);
  }, []);

  const handleClickContinue = useCallback(product => {
    track(() => ([
      evSearchCrossSiteRecoClick, { product, proceedToTrustedRetailer: true }
    ]));
    setModal(null);
  }, []);

  useEffect(() => {
    track(() => ([evSearchCrossSiteRecoImpression, trustedRetailers]));
  }, [trustedRetailers]);

  function makeProductImage(modal) {
    const carouseImages = modal.styles.find(v => v.styleId === modal.styleId);
    const imageUrls = carouseImages?.images ? carouseImages.images : [{ imageId: modal.thumbnailImageUrl }];
    const images = imageUrls.map((i, k) => <img key={k} src={constructMSAImageUrl(i.imageId, MSA_IMAGE_DIMENSION)} alt={i.imageId} />);
    return <ImageCarousel
      slidesToScroll={1}
      slidesToShow={1}
      autoplay={false}
      wrapAround={true}
      overlapDots={true}>
      {images}
    </ImageCarousel>;
  }

  const makeModal = modal => {
    const { storeUrl, styleId, store, productSeoUrl, productName, brandName, price } = modal;
    return <MelodyModal
      onRequestClose={() => handleClickStay(modal)}
      className={css.modal}
      contentLabel="Trusted Retailers"
      isOpen={!!modal}>
      <section className={css.modalCarousel}>{makeProductImage(modal)}</section>
      <section className={css.modalContent}>
        <div className={cn(header, css.header)}>
          <ul>
            {store && <li><CrossSiteLabel store={store}/></li>}
            <li className={css.trustedRetailer}><TrustedRetailerBanner styleId={styleId}/></li>
          </ul>
        </div>
        <h2>Now leaving {shortName}</h2>
        <div>
          <p className={css.copy}>
            We don't carry this product, but <a href={storeUrl}>{`${store}.com`}</a> does! We like them. We think you will too!
          </p>
          <button
            className={css.trustedInfo}
            aria-expanded={!!details}
            onClick={() => handleDetailsClick(details)}
            type="button">Why are they a Trusted Retailer?</button>
          {details && (
            <ul>
              <li><strong>Reputable Business:</strong> This is a Zappos sister site that carries genuine products.</li>
              <li><strong>Use your Zappos Login:</strong> Nothing new to remember, just use your same Zappos username and password.</li>
              <li><strong>Great customer benefits:</strong> Friend customer service, 2 day free shipping, 30 day return policy.</li>
            </ul>
          )}
        </div>
        <div className={css.modalBtnContainer}>
          <button
            data-test-id={testId('searchResultsCrossSiteModalStay')}
            type="button"
            className={css.btnStay}
            onClick={() => handleClickStay(modal)}>
            Stay on {shortName}
          </button>
          <a
            onClick={() => handleClickContinue(modal)}
            data-test-id={testId('searchResultsCrossSiteModalContinue')}
            className={css.btnContinue}
            href={productSeoUrl}
            target="_blank"
            rel="noopener noreferrer">
            Continue to {store}
          </a>
        </div>
        <div className={css.btnWrapper}>
          {brandName && <p>{brandName}</p>}
          {productName && <p className={css.productName}>{productName}</p>}
          {price && <p className={css.price}>{price}</p>}
        </div>
      </section>
    </MelodyModal>;
  };

  if (trustedRetailers.length) {
    return (
      <>
        {trustedRetailers.map((v, i) => (
          <ProductComponent
            key={`${v.styleId}-${v.store}`}
            componentStyle="searchProductCrossSite"
            crossSite={v.store}
            onComponentClick={e => handleClickCard(v, i, e)}
            additionalClasses={additionalClasses}
            hydraSearchColorSwatches={hydraSearchColorSwatches}
            cardData={v}
            index={i}
            msaImageParams={msaImageParams}
            hydraHdSearchImages={hydraHdSearchImages}
            melodyCardTestId={testId('searchResultsCrossSiteProduct')}
            showRatingStars={false}
            shouldLazyLoad={false}/>
        ))}
        {!!modal && makeModal(modal)}
      </>
    );
  }

  return null;
};

export default withErrorBoundary('CrossSiteProducts', CrossSiteProducts);
